import { inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { SafeAny } from '../../../types/safe-any';

import { ItemProcessesActions } from './item-processes.actions';
import { IItemProcess, ItemProcessesService, ItemProcessGroup } from './item-processes.service';

// Add Item Process
export const addItemProcess = createEffect(
  (actions$ = inject(Actions), itemProcessesService = inject(ItemProcessesService)) =>
    actions$.pipe(
      ofType(ItemProcessesActions.addItemProcess),
      switchMap(({ payload }) =>
        itemProcessesService.addItemProcess(payload).pipe(
          map((createdItem: IItemProcess) =>
            ItemProcessesActions.addItemProcessSuccess({ createdItem }),
          ),
          catchError((error) => of(ItemProcessesActions.addItemProcessFailure({ error }))),
        ),
      ),
    ),
  { functional: true },
);

// Load Item Processes
export const loadItemProcesses = createEffect(
  (actions$ = inject(Actions), itemProcessesService = inject(ItemProcessesService)) =>
    actions$.pipe(
      ofType(ItemProcessesActions.loadItemProcesses),
      switchMap(({ payload }) =>
        itemProcessesService.getItemProcesses(payload).pipe(
          map((groups: ItemProcessGroup[]) =>
            ItemProcessesActions.loadItemProcessesSuccess({ groups }),
          ),
          catchError((error) => of(ItemProcessesActions.loadItemProcessesFailure({ error }))),
        ),
      ),
    ),
  { functional: true },
);

export const loadItemProcessCategories = createEffect(
  (actions$ = inject(Actions), itemProcessesService = inject(ItemProcessesService)) => {
    return actions$.pipe(
      ofType(ItemProcessesActions.loadItemProcessCategories),
      switchMap(() =>
        itemProcessesService.loadItemProcessCategories().pipe(
          map((categories: SafeAny) =>
            ItemProcessesActions.loadItemProcessCategoriesSuccess({ categories }),
          ),
          catchError((error) =>
            of(ItemProcessesActions.loadItemProcessCategoriesFailure({ error })),
          ),
        ),
      ),
    );
  },
  { functional: true },
);

// Load Item Process Categories Success
export const loadItemProcessCategoriesSuccess = createEffect(
  (actions$ = inject(Actions), itemProcessesService = inject(ItemProcessesService)) =>
    actions$.pipe(
      ofType(ItemProcessesActions.loadItemProcessCategoriesSuccess),
      switchMap(({ categories }) => {
        // Perform any additional logic if needed
        return of(
          ItemProcessesActions.loadItemProcesses({
            payload: {
              q: '',
              limit: 5,
              offset: 1,
              sort: 'itemId',
            },
          }),
        );
      }),
    ),
  { functional: true },
);

// Effect to handle updating the base price for a category
export const updateItemProcessBasePrice = createEffect(
  (actions$ = inject(Actions), itemProcessesService = inject(ItemProcessesService)) =>
    actions$.pipe(
      ofType(ItemProcessesActions.updateItemProcessBasePrice),
      switchMap(({ categoryKey, basePrice }) =>
        itemProcessesService.updateBasePriceForCategory(categoryKey, basePrice).pipe(
          map(
            () =>
              ItemProcessesActions.updateItemProcessBasePriceSuccess({ categoryKey, basePrice }), // Pass key/price to success action
          ),
          catchError((error) =>
            of(ItemProcessesActions.updateItemProcessBasePriceFailure({ error })),
          ),
        ),
      ),
    ),
  { functional: true },
);

export const updateItemProcess = createEffect(
  (actions$ = inject(Actions), itemProcessesService = inject(ItemProcessesService)) =>
    actions$.pipe(
      ofType(ItemProcessesActions.updateItemProcess),
      switchMap(({ payload, itemId }) =>
        itemProcessesService.updateItemProcess(payload, itemId).pipe(
          map((item: IItemProcess) => ItemProcessesActions.updateItemProcessSuccess({ item })),
          catchError((error) => of(ItemProcessesActions.updateItemProcessFailure({ error }))),
        ),
      ),
    ),
  { functional: true },
);

// Load Specific Item Group Page (Now includes sort)
export const loadItemGroupPage = createEffect(
  (actions$ = inject(Actions), itemProcessesService = inject(ItemProcessesService)) =>
    actions$.pipe(
      ofType(ItemProcessesActions.loadItemGroupPage),
      switchMap(({ payload }) => {
        const { categoryKey, q, limit, offset, sort } = payload;
        return itemProcessesService.getSingleItemGroup(categoryKey, q, limit, offset, sort).pipe(
          map((group: ItemProcessGroup | null) =>
            ItemProcessesActions.loadItemGroupPageSuccess({ group }),
          ),
          catchError((error) => of(ItemProcessesActions.loadItemGroupPageFailure({ error }))),
        );
      }),
    ),
  { functional: true },
);

// Sort Item Group
export const sortItemGroup = createEffect(
  (actions$ = inject(Actions), itemProcessesService = inject(ItemProcessesService)) =>
    actions$.pipe(
      ofType(ItemProcessesActions.sortItemGroup),
      switchMap(({ payload }) => {
        const { categoryKey, q, sort } = payload;
        const apiSort = sort.direction
          ? `${sort.direction === 'desc' ? '-' : ''}${sort.active}`
          : undefined;
        const limit = 5;
        const offset = 1;
        return itemProcessesService.getSingleItemGroup(categoryKey, q, limit, offset, apiSort).pipe(
          map((group: ItemProcessGroup | null) =>
            ItemProcessesActions.sortItemGroupSuccess({ group }),
          ),
          catchError((error) => of(ItemProcessesActions.sortItemGroupFailure({ error }))),
        );
      }),
    ),
  { functional: true },
);

// Delete Item Process
export const deleteItemProcess = createEffect(
  (actions$ = inject(Actions), itemProcessesService = inject(ItemProcessesService)) =>
    actions$.pipe(
      ofType(ItemProcessesActions.deleteItemProcess),
      switchMap(({ itemId, categoryKey, currentPage }) =>
        itemProcessesService.deleteItemProcess(itemId).pipe(
          switchMap(() => [
            ItemProcessesActions.deleteItemProcessSuccess({ itemId }),
            // Reload the specific group with current pagination and search
            ItemProcessesActions.loadItemGroupPage({
              payload: {
                categoryKey,
                limit: currentPage.limit,
                offset: currentPage.offset,
                q: currentPage.searchTerm,
                sort: currentPage.sort,
              },
            }),
          ]),
          catchError((error) => of(ItemProcessesActions.deleteItemProcessFailure({ error }))),
        ),
      ),
    ),
  { functional: true },
);

export const loadOnlyItemProcessCategories = createEffect(
  (actions$ = inject(Actions), itemProcessesService = inject(ItemProcessesService)) =>
    actions$.pipe(
      ofType(ItemProcessesActions.loadOnlyItemProcessCategories),
      switchMap(() =>
        itemProcessesService.loadItemProcessCategories().pipe(
          map((categories: SafeAny) =>
            ItemProcessesActions.loadOnlyItemProcessCategoriesSuccess({ categories }),
          ),
          catchError((error) =>
            of(ItemProcessesActions.loadOnlyItemProcessCategoriesFailure({ error })),
          ),
        ),
      ),
    ),
  { functional: true },
);
